import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-countdown-timer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="countdown-timer">
      <div class="time-unit" [ngClass]="{ warning: isWarning }">
        <span class="time-value">{{ hours }}</span>
      </div>
      <span class="time-separator">:</span>
      <div class="time-unit" [ngClass]="{ warning: isWarning }">
        <span class="time-value">{{ minutes }}</span>
      </div>
      <span class="time-separator">:</span>
      <div class="time-unit" [ngClass]="{ warning: isWarning }">
        <span class="time-value">{{ seconds }}</span>
      </div>
    </div>
  `,
  styles: [
    `
      .countdown-timer {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 9px;
      }

      .time-unit {
        background-color: #eaebf3;
        border-radius: 8px;
        width: 56px;
        height: 49px;
        text-align: center;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .time-value {
        color: var(--color-primary);
        font-size: 24px;
        font-weight: 600;
        line-height: 17px;
      }

      .time-separator {
        font-weight: 600;
        font-size: 24px;
        color: var(--color-primary);
      }

      /* Animation for when time is running low */
      .time-unit.warning {
        animation: pulse 1s infinite;
      }

      @keyframes pulse {
        0% {
          transform: scale(1);
        }
        50% {
          transform: scale(1.05);
        }
        100% {
          transform: scale(1);
        }
      }

      /* Responsive design */
      @media (max-width: 768px) {
        .time-unit {
          min-width: 40px;
          padding: 8px 10px;
        }

        .time-value {
          font-size: 16px;
        }

        .time-separator {
          font-size: 18px;
        }
      }
    `
  ]
})
export class CountdownTimerComponent implements OnInit, OnDestroy, OnChanges {
  @Input() timeString: string = '00:00:00';
  @Input() mode: 'countdown' | 'countup' = 'countdown';
  @Input() startTime: string | Date | null = null;
  @Output() elapsedSecondsChange = new EventEmitter<number>();

  hours: string = '00';
  minutes: string = '00';
  seconds: string = '00';
  isWarning: boolean = false;

  private interval: any;

  ngOnInit() {
    this.resetAndInitializeTimer();
  }

  ngOnChanges(_changes: SimpleChanges) {
    this.resetAndInitializeTimer();
  }

  ngOnDestroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  private parseTime() {
    const timeParts = this.timeString.split(':');
    if (timeParts.length === 3) {
      this.hours = timeParts[0].padStart(2, '0');
      this.minutes = timeParts[1].padStart(2, '0');
      this.seconds = timeParts[2].padStart(2, '0');
    }
  }

  private resetAndInitializeTimer() {
    if (this.interval) {
      clearInterval(this.interval);
    }

    if (this.mode === 'countup') {
      this.isWarning = false;
      this.updateCountupDisplay();
      this.startCountupTimer();
      return;
    }

    this.parseTime();
    this.startCountdownTimer();
  }

  private startCountdownTimer() {
    this.interval = setInterval(() => {
      let totalSeconds = this.getTotalSeconds();

      if (totalSeconds > 0) {
        totalSeconds--;
        this.updateDisplay(totalSeconds);
      } else {
        clearInterval(this.interval);
      }
    }, 1000);
  }

  private startCountupTimer() {
    this.interval = setInterval(() => {
      this.updateCountupDisplay();
    }, 1000);
  }

  private updateCountupDisplay() {
    const elapsedSeconds = this.getElapsedSeconds();
    this.updateDisplay(elapsedSeconds);
    this.elapsedSecondsChange.emit(elapsedSeconds);
  }

  private getElapsedSeconds(): number {
    if (!this.startTime) {
      return 0;
    }

    const start = new Date(this.startTime);
    if (Number.isNaN(start.getTime())) {
      return 0;
    }

    const elapsedSeconds = Math.floor((Date.now() - start.getTime()) / 1000);
    return Math.max(0, elapsedSeconds);
  }

  private getTotalSeconds(): number {
    return parseInt(this.hours) * 3600 + parseInt(this.minutes) * 60 + parseInt(this.seconds);
  }

  private updateDisplay(totalSeconds: number) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.hours = hours.toString().padStart(2, '0');
    this.minutes = minutes.toString().padStart(2, '0');
    this.seconds = seconds.toString().padStart(2, '0');

    // Warning style applies only to countdown mode.
    this.isWarning = this.mode === 'countdown' && totalSeconds <= 300 && totalSeconds > 0;
  }
}
